/**
 * Print preview (screenshots/86; brief §2.13 Print): every page full-screen + the options panel (Destination, Paper Letter /
 * A4 and Landscape / Portrait, Theme colors / Grayscale-safe, Include, Layout). Pages come from `printPlan`: a widget is
 * never split across pages (ROWS_PER_PAGE), and each page repeats the title, the filters, "Data as of …", the link (if
 * shared) and the footer with sources and the money caveat. Pages show the real widgets
 * read-only (PrintGrid: the same WidgetFrame as the grid); the appendix prints every chart's numbers as tables.
 * Save PDF uses the browser's print engine: the view portals to <body>, `@page` follows the paper, and the print rules in
 * app.css hide the app. Choices are saved on the dashboard. From the handoff starter's src/analytics/PrintView.tsx.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Download, FileDown, Lock } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { useShallow } from "zustand/react/shallow";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { cellsToPx, GRID_W, layoutHeight } from "../overview/grid/layoutEngine";
import { WidgetFrame } from "../overview/WidgetFrame";
import { useOverview } from "../overview/overviewStore";
import { toTable } from "../overview/charts/chartTypes";
import { entryOf } from "../overview/catalog";
import type { DatasetResult, LayoutItem } from "../overview/types";
import { printPlan, type Dashboard, type PrintSetup } from "./dashboards";
import { filterChip, type PageFilters } from "./pageFilters";

/** Paper size in inches (portrait); margins are 0.5 in on every side. */
const PAPER = { letter: [8.5, 11], a4: [8.27, 11.69] } as const;
const pagePx = (p: PrintSetup) => { const [w, h] = PAPER[p.paper]; return p.orientation === "landscape" ? { w: h * 96, h: w * 96 } : { w: w * 96, h: h * 96 }; };
export const printZoom = (p: PrintSetup) => (pagePx(p).w - 96) / GRID_W;
export const pageCss = (p: PrintSetup) => `@page { size: ${p.paper} ${p.orientation}; margin: 0; }`;

/** Every chart's numbers for the appendix, from what each chart shows now (same query, filters and cross-filter). */
export function appendixTables(items: LayoutItem[], datasets: Record<string, DatasetResult>, views: Map<string, { ds: DatasetResult; title: string }>) {
  return items.filter((i) => i.config.settings && !(i.config as { redacted?: boolean }).redacted).map((i) => {
    const ds = views.get(i.id)?.ds ?? datasets[i.id];
    const title = views.get(i.id)?.title ?? i.config.title ?? i.config.settings?.labels.title ?? entryOf(i.type).title;
    if (!ds) return { id: i.id, title, table: null };
    if (ds.columns?.length && ds.rows.length) {
      const fmt = (v: unknown, f?: string) => (typeof v === "number" ? (f === "usd" ? `$${Math.round(v).toLocaleString("en-US")}` : v.toLocaleString("en-US")) : String(v ?? "—"));
      return { id: i.id, title, table: { columns: ds.columns.map((c) => c.label), rows: ds.rows.map((r) => ds.columns.map((c) => fmt(r[c.id], c.format))) } };
    }
    return { id: i.id, title, table: toTable(ds.shape) };
  });
}

/** One printed page's widgets, read-only, at their cells (rows start at the page's first row). */
function PrintGrid({ items }: { items: LayoutItem[] }) {
  const { feeds, snapshot, group } = useOverview(useShallow((s) => ({ feeds: s.feeds, snapshot: s.snapshot, group: s.group })));
  return (
    <div className="an-print__grid" style={{ height: cellsToPx({ x: 0, y: 0, w: 12, h: Math.max(1, layoutHeight(items)) }).height }}>
      {items.map((it) => {
        const p = cellsToPx(it);
        return <WidgetFrame key={it.id} item={it} colW={80} group={group} feeds={feeds} snapshot={snapshot} customizing={false} selected={false} removing={false}
          style={{ transform: `translate(${p.left}px, ${p.top}px)`, width: p.width, height: p.height }} />;
      })}
    </div>
  );
}

export function PrintView({ dashboard, orgName, filters, link, views, onClose, onSaveSetup }: {
  dashboard: Pick<Dashboard, "title" | "layout" | "print" | "link" | "filters" | "id" | "version" | "updatedAt">;
  orgName: string | null; filters: PageFilters; link?: string;
  /** What each chart showed when Print was opened (widgetViews), for the appendix. */
  views: Map<string, { ds: DatasetResult; title: string }>;
  onClose: () => void; onSaveSetup: (p: PrintSetup) => void;
}) {
  const [setup, setSetup] = useState<PrintSetup>(dashboard.print);
  const [header, setHeader] = useState(true);
  const items = useOverview((s) => s.snapshot) ? (dashboard.layout as LayoutItem[]) : [];
  const datasets = useOverview((s) => s.datasets);
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(box, true, { onEscape: onClose });
  const filtersText = [orgName, ...(["since", "topic", "wards"] as const).map((k) => filterChip(k, filters))].filter(Boolean).join(" · ");
  const asOf = useMemo(() => new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" }), []);
  const plan = useMemo(() => printPlan({ ...(dashboard as Dashboard), layout: items, print: setup }, { asOf, filtersText, origin: location.origin }), [dashboard, items, setup, asOf, filtersText]);
  const tables = useMemo(() => (setup.appendix ? appendixTables(items, datasets, views) : []), [setup.appendix, items, datasets, views]);
  const total = plan.pages.length + (plan.appendix ? 1 : 0);
  const size = pagePx(setup);
  const linkText = link ?? plan.header.link?.replace(/^https?:\/\//, "");

  useEffect(() => { document.documentElement.classList.add("is-printing"); return () => document.documentElement.classList.remove("is-printing"); }, []);
  const footer = (n: number) => <footer className="an-page__foot"><span>{plan.footer}</span><b>Page {n} of {total}</b></footer>;

  return createPortal(
    <div ref={box} className={`an-print${setup.colors === "grayscale" ? " is-gray" : ""}`} role="dialog" aria-modal="true" aria-label={`Print ${dashboard.title}`}>
      <style>{pageCss(setup)}</style>
      <div className="an-print__pages">
        {plan.pages.map((page, i) => (
          <article key={i} className="an-page" aria-label={`Page ${i + 1} of ${total}`} style={{ width: size.w, height: size.h, ["--print-zoom" as string]: printZoom(setup) }}>
            <header className="an-page__head">
              <div><h1>{plan.header.title}</h1>{header && <p>{plan.header.filters}</p>}</div>
              {header && <div className="an-page__asof"><strong>{plan.header.asOf}</strong>{linkText && <small>{linkText}</small>}</div>}
            </header>
            <div className="an-page__grid"><PrintGrid items={page as LayoutItem[]} /></div>
            {footer(i + 1)}
          </article>
        ))}
        {plan.appendix && (
          <article className="an-page an-page--appendix" aria-label={`Page ${total} of ${total}`} style={{ width: size.w, minHeight: size.h }}>
            <h2>Appendix · data tables</h2>
            <p>Every chart’s numbers, for readers who can’t use the charts.</p>
            {tables.map((t) => (
              <section key={t.id}>
                {tables.length > 1 && <h3>{t.title}</h3>}
                {t.table ? (
                  <table><thead><tr>{t.table.columns.map((c, k) => <th key={k} scope="col">{c}</th>)}</tr></thead>
                    <tbody>{t.table.rows.map((r, k) => <tr key={k}>{r.map((c, j) => <td key={j}>{String(c)}</td>)}</tr>)}</tbody></table>
                ) : <p>No data for the current filters.</p>}
              </section>
            ))}
            {footer(total)}
          </article>
        )}
      </div>
      <aside className="an-print__opts" aria-label="Print options">
        <header><h2>Print “{dashboard.title}”</h2><span>{total} pages</span></header>
        <div className="an-print__body">
          <h3 className="eyebrow">Destination</h3>
          <Dropdown className="an-pill an-pill--block has-icon" label="Destination" value="pdf" onChange={() => {}} icon={<FileDown size={15} aria-hidden />}
            chevron={<ChevronDown size={14} aria-hidden />} options={[{ value: "pdf", label: "Save as PDF" }]} />
          <h3 className="eyebrow">Paper</h3>
          <div className="an-print__row">
            <Seg label="Paper" value={setup.paper} onChange={(paper) => setSetup({ ...setup, paper })} options={[["letter", "Letter"], ["a4", "A4"]]} />
            <Seg label="Orientation" value={setup.orientation} onChange={(orientation) => setSetup({ ...setup, orientation })} options={[["landscape", "Landscape"], ["portrait", "Portrait"]]} />
          </div>
          <h3 className="eyebrow">Colors</h3>
          <Seg label="Colors" block value={setup.colors} onChange={(colors) => setSetup({ ...setup, colors })} options={[["theme", "Theme colors"], ["grayscale", "Grayscale-safe"]]} />
          <h3 className="eyebrow">Include</h3>
          <Toggle on={header} onChange={setHeader} label="Filters and data date in the header" />
          <Toggle on={setup.appendix} onChange={(appendix) => setSetup({ ...setup, appendix })} label="Data tables appendix" />
          <Toggle on locked label="Sources and caveats" />
          <h3 className="eyebrow">Layout</h3>
          <Toggle on fixed label="Never split a widget across pages" />
        </div>
        <footer className="an-modal__foot">
          <button className="btn btn--secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={() => { onSaveSetup(setup); window.print(); }}><Download size={15} aria-hidden />Save PDF</button>
        </footer>
      </aside>
    </div>, document.body);
}

function Seg<T extends string>({ value, options, onChange, label, block }: { value: T; options: [T, string][]; onChange: (v: T) => void; label: string; block?: boolean }) {
  return (
    <div className={`view-switch an-seg${block ? " an-seg--block" : ""}`} role="radiogroup" aria-label={label}>
      {options.map(([id, l]) => <button key={id} role="radio" aria-checked={id === value} onClick={() => onChange(id)}>{l}</button>)}
    </div>
  );
}

function Toggle({ on, onChange, label, locked, fixed }: { on: boolean; onChange?: (v: boolean) => void; label: string; locked?: boolean; fixed?: boolean }) {
  const still = locked || fixed;
  return (
    <div className={`an-toggle${locked ? " is-locked" : ""}`}>
      <button type="button" className="switch switch--md" role="switch" aria-checked={on} aria-disabled={still || undefined} aria-label={label} onClick={() => { if (!still) onChange?.(!on); }} />
      <span aria-hidden>{label}</span>
      {locked && <Lock size={14} aria-hidden className="an-toggle__lock" />}
    </div>
  );
}
